import React, { useState, useEffect } from 'react';
import {
    Modal,
    Form,
    Input,
    InputNumber,
    DatePicker,
    TimePicker,
    Select,
    Button,
    Row,
    Col,
    Card,
    Typography,
    Divider,
    Space,
    message,
    Table,
    Tag,
    Divider as AntDivider
} from 'antd';
import {
    EditOutlined,
    CalculatorOutlined,
    SaveOutlined,
    SendOutlined,
    DollarOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

function LeadEditModal({ visible, lead, onClose, onSave, onSendQuote }) {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [calculations, setCalculations] = useState({
        baseAmount: 0,
        perPersonRate: 0,
        totalGuests: 0,
        subtotal: 0,
        tax: 0,
        total: 0
    });
    const [selectedDishes, setSelectedDishes] = useState([]);
    const [availableDishes, setAvailableDishes] = useState([]);

    useEffect(() => {
        if (lead && visible) {
            // Initialize form with lead data
            form.setFieldsValue({
                event_type: lead.event_type,
                event_date: lead.event_date ? dayjs(lead.event_date) : null,
                event_time: lead.event_time ? dayjs(lead.event_time, 'HH:mm') : null,
                venue: lead.venue,
                guest_count: lead.guest_count,
                special_requirements: lead.special_requirements,
                contact_phone: lead.contact_phone,
                contact_email: lead.contact_email,
                vendor_notes: lead.vendor_notes,
                estimated_total: lead.estimated_total,
                final_total: lead.final_total
            });

            // Set selected dishes
            if (lead.selected_dishes) {
                setSelectedDishes(lead.selected_dishes);
            }

            // Calculate initial totals
            calculateTotals(lead.guest_count, lead.selected_dishes || []);
        }
    }, [lead, visible, form]);

    useEffect(() => {
        loadAvailableDishes();
    }, []);

    const loadAvailableDishes = async () => {
        try {
            const response = await api.getMenus({ per_page: 100 });
            if (response.success) {
                setAvailableDishes(response.data.data || []);
            }
        } catch (error) {
            console.error('Failed to load dishes:', error);
        }
    };

    const calculateTotals = (guestCount, dishes) => {
        const perPersonRate = 500; // Base rate per person
        const baseAmount = guestCount * perPersonRate;
        
        // Calculate dish costs
        let dishTotal = 0;
        dishes.forEach(dish => {
            const dishPrice = dish.price || 0;
            const quantity = dish.quantity || 1;
            dishTotal += dishPrice * quantity;
        });

        const subtotal = baseAmount + dishTotal;
        const tax = subtotal * 0.18; // 18% tax
        const total = subtotal + tax;

        setCalculations({
            baseAmount,
            perPersonRate,
            totalGuests: guestCount,
            subtotal,
            tax,
            total
        });

        // Update form with calculated values
        form.setFieldsValue({
            estimated_total: total,
            final_total: total
        });
    };

    const handleGuestCountChange = (value) => {
        calculateTotals(value, selectedDishes);
    };

    const handleDishChange = (dishId, field, value) => {
        const updatedDishes = selectedDishes.map(dish => 
            dish.id === dishId ? { ...dish, [field]: value } : dish
        );
        setSelectedDishes(updatedDishes);
        calculateTotals(form.getFieldValue('guest_count') || 0, updatedDishes);
    };

    const addDish = (dishId) => {
        const dish = availableDishes.find(d => d.id === dishId);
        if (dish) {
            const newDish = {
                id: dish.id,
                name: dish.name,
                price: dish.price || 0,
                quantity: 1
            };
            const updatedDishes = [...selectedDishes, newDish];
            setSelectedDishes(updatedDishes);
            calculateTotals(form.getFieldValue('guest_count') || 0, updatedDishes);
        }
    };

    const removeDish = (dishId) => {
        const updatedDishes = selectedDishes.filter(dish => dish.id !== dishId);
        setSelectedDishes(updatedDishes);
        calculateTotals(form.getFieldValue('guest_count') || 0, updatedDishes);
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();
            
            const updatedLead = {
                ...values,
                event_date: values.event_date?.format('YYYY-MM-DD'),
                event_time: values.event_time?.format('HH:mm'),
                selected_dishes: selectedDishes,
                estimated_total: calculations.total,
                final_total: calculations.total,
                status: 'quoted'
            };

            await api.updateLead(lead.id, updatedLead);
            message.success('Lead updated successfully');
            onSave(updatedLead);
        } catch (error) {
            message.error('Failed to update lead');
        } finally {
            setLoading(false);
        }
    };

    const handleSendQuote = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();
            
            const updatedLead = {
                ...values,
                event_date: values.event_date?.format('YYYY-MM-DD'),
                event_time: values.event_time?.format('HH:mm'),
                selected_dishes: selectedDishes,
                estimated_total: calculations.total,
                final_total: calculations.total,
                status: 'quoted',
                quoted_at: new Date().toISOString()
            };

            await api.updateLead(lead.id, updatedLead);
            message.success('Quote sent successfully');
            onSendQuote(updatedLead);
        } catch (error) {
            message.error('Failed to send quote');
        } finally {
            setLoading(false);
        }
    };

    const dishColumns = [
        {
            title: 'Dish',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Price',
            dataIndex: 'price',
            key: 'price',
            render: (price) => `₹${price || 0}`,
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            render: (quantity, record) => (
                <InputNumber
                    min={1}
                    value={quantity}
                    onChange={(value) => handleDishChange(record.id, 'quantity', value)}
                    style={{ width: 80 }}
                />
            ),
        },
        {
            title: 'Total',
            key: 'total',
            render: (_, record) => `₹${(record.price || 0) * (record.quantity || 1)}`,
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Button
                    type="link"
                    danger
                    onClick={() => removeDish(record.id)}
                >
                    Remove
                </Button>
            ),
        },
    ];

    return (
        <Modal
            title={
                <div className="flex items-center">
                    <EditOutlined className="mr-2" />
                    Edit Lead & Create Quote
                </div>
            }
            open={visible}
            onCancel={onClose}
            width={1200}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Cancel
                </Button>,
                <Button
                    key="save"
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={loading}
                    onClick={handleSave}
                >
                    Save Changes
                </Button>,
                <Button
                    key="send"
                    type="primary"
                    icon={<SendOutlined />}
                    loading={loading}
                    onClick={handleSendQuote}
                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                >
                    Send Quote
                </Button>,
            ]}
        >
            <Form form={form} layout="vertical">
                <Row gutter={[16, 16]}>
                    {/* Left Column - Lead Details */}
                    <Col xs={24} lg={12}>
                        <Card title="Lead Details" size="small">
                            <Row gutter={[16, 16]}>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="event_type"
                                        label="Event Type"
                                        rules={[{ required: true, message: 'Please select event type' }]}
                                    >
                                        <Select placeholder="Select event type">
                                            <Option value="wedding">Wedding</Option>
                                            <Option value="corporate">Corporate</Option>
                                            <Option value="birthday">Birthday</Option>
                                            <Option value="anniversary">Anniversary</Option>
                                            <Option value="other">Other</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="guest_count"
                                        label="Guest Count"
                                        rules={[{ required: true, message: 'Please enter guest count' }]}
                                    >
                                        <InputNumber
                                            min={1}
                                            style={{ width: '100%' }}
                                            onChange={handleGuestCountChange}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="event_date"
                                        label="Event Date"
                                        rules={[{ required: true, message: 'Please select event date' }]}
                                    >
                                        <DatePicker style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="event_time"
                                        label="Event Time"
                                        rules={[{ required: true, message: 'Please select event time' }]}
                                    >
                                        <TimePicker style={{ width: '100%' }} format="HH:mm" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24}>
                                    <Form.Item
                                        name="venue"
                                        label="Venue"
                                        rules={[{ required: true, message: 'Please enter venue' }]}
                                    >
                                        <Input placeholder="Enter venue address" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24}>
                                    <Form.Item
                                        name="special_requirements"
                                        label="Special Requirements"
                                    >
                                        <TextArea rows={3} placeholder="Any special requirements or notes" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="contact_phone"
                                        label="Contact Phone"
                                        rules={[{ required: true, message: 'Please enter contact phone' }]}
                                    >
                                        <Input placeholder="Enter phone number" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="contact_email"
                                        label="Contact Email"
                                        rules={[{ required: true, message: 'Please enter contact email' }]}
                                    >
                                        <Input placeholder="Enter email address" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24}>
                                    <Form.Item
                                        name="vendor_notes"
                                        label="Vendor Notes"
                                    >
                                        <TextArea rows={3} placeholder="Internal notes for this lead" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>
                    </Col>

                    {/* Right Column - Menu & Pricing */}
                    <Col xs={24} lg={12}>
                        <Card title="Menu & Pricing" size="small">
                            <div className="mb-4">
                                <Text strong>Add Dishes:</Text>
                                <Select
                                    placeholder="Select dishes to add"
                                    style={{ width: '100%', marginTop: 8 }}
                                    onChange={addDish}
                                    allowClear
                                >
                                    {availableDishes.map(dish => (
                                        <Option key={dish.id} value={dish.id}>
                                            {dish.name} - ₹{dish.price || 0}
                                        </Option>
                                    ))}
                                </Select>
                            </div>

                            <Table
                                columns={dishColumns}
                                dataSource={selectedDishes}
                                rowKey="id"
                                pagination={false}
                                size="small"
                                scroll={{ y: 200 }}
                            />

                            <AntDivider />

                            {/* Pricing Summary */}
                            <Card size="small" title={
                                <div className="flex items-center">
                                    <CalculatorOutlined className="mr-2" />
                                    Pricing Summary
                                </div>
                            }>
                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <Text>Base Rate ({calculations.totalGuests} guests × ₹{calculations.perPersonRate})</Text>
                                        <Text>₹{calculations.baseAmount}</Text>
                                    </div>
                                    <div className="flex justify-between">
                                        <Text>Dish Costs</Text>
                                        <Text>₹{calculations.subtotal - calculations.baseAmount}</Text>
                                    </div>
                                    <div className="flex justify-between">
                                        <Text>Subtotal</Text>
                                        <Text>₹{calculations.subtotal}</Text>
                                    </div>
                                    <div className="flex justify-between">
                                        <Text>Tax (18%)</Text>
                                        <Text>₹{calculations.tax.toFixed(2)}</Text>
                                    </div>
                                    <Divider />
                                    <div className="flex justify-between">
                                        <Text strong className="text-lg">
                                            <DollarOutlined className="mr-1" />
                                            Total
                                        </Text>
                                        <Text strong className="text-lg text-green-600">
                                            ₹{calculations.total.toFixed(2)}
                                        </Text>
                                    </div>
                                </div>
                            </Card>
                        </Card>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
}

export default LeadEditModal;
